import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from "@mui/material";
import { useId } from "react";

type Option = { key: number; label: string };

type LabeledCheckboxSelectProps = Omit<
  SelectProps<number[]>,
  "multiple" | "value" | "onChange" | "renderValue" | "children"
> & {
  value: number[];
  onChange: (newValue: number[]) => void;
  options: Option[];
  required?: boolean;
  label: string;
  fullWidth?: boolean;
};

const LabeledCheckboxSelect = ({
  sx,
  label,
  value,
  required,
  fullWidth,
  options,
  onChange,
  ...props
}: LabeledCheckboxSelectProps) => {
  const id = useId();

  const showRequiredMark = !!required && value.length === 0;
  const computedLabel = `${label}${showRequiredMark ? " *" : ""}`;

  return (
    <FormControl sx={{ width: fullWidth ? "100%" : undefined, ...sx }} >
      <InputLabel shrink id={id} size="small">
        {label}
        {showRequiredMark && (
          <Typography color="error" component="span"> {" "}* </Typography>
        )}
      </InputLabel>

      <Select<number[]>
        {...props}
        multiple
        labelId={id}
        label={computedLabel}
        value={value}
        displayEmpty
        renderValue={(selected) => {
          if (selected.length === 0) return 'Any';
          return options
            .filter((o) => selected.includes(o.key))
            .map((o) => o.label)
            .join(', ');
        }}
        onChange={(e) => {
          let { value } = e.target;
          value =
            typeof value === 'string'
              ? value.split(',').map((v) => parseInt(v))
              : value;
          onChange(value);
        }}
        size="small"
      >
        {options.map(({ key, label }) => (
          <MenuItem key={key} value={key}>
            <Checkbox checked={value.includes(Number(key))} />
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LabeledCheckboxSelect;
